import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Drawer, Input, Empty, Spin, Typography, Button, Space, message, Tag } from 'antd';
import { ReadOutlined, FilePdfOutlined, FileMarkdownOutlined, LinkOutlined, PlusOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { searchKnowledge } from '@/api/knowledge';
import type { Knowledge } from '@/api/types';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';

const { Text } = Typography;

// Right-anchored drawer that lists every Knowledge base in the merchant
// scope and lets the user one-click "Link" each one to the current agent.
// Already-linked KBs are hidden so the user can't double-link by accident.
//
// Per project convention (drawers > modals for picker flows): keeps the
// agent detail page visible behind so the SME knows what they're linking
// to without losing context.

interface LinkKnowledgeDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Set of KnowledgeIds already linked to the agent (will be hidden from picker). */
  alreadyLinkedIds: Set<number>;
  agentDisplayName: string;
}

export const LinkKnowledgeDrawer = observer(function LinkKnowledgeDrawer({
  open,
  onClose,
  alreadyLinkedIds,
  agentDisplayName,
}: LinkKnowledgeDrawerProps) {
  const { agentDetailStore: detail, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  const [allKbs, setAllKbs] = useState<Knowledge[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [linkingId, setLinkingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    searchKnowledge({ pageSize: 200 })
      .then((res) => {
        if (cancelled) return;
        setAllKbs(res.data ?? []);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load knowledge bases');
        setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [open]);

  const candidates = allKbs.filter((kb) => {
    if (alreadyLinkedIds.has(kb.id)) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (kb.title ?? '').toLowerCase().includes(q) ||
      (kb.description ?? '').toLowerCase().includes(q)
    );
  });

  async function handleLink(kb: Knowledge) {
    setLinkingId(kb.id);
    try {
      await detail.linkKnowledge(kb.id);
      message.success(`Linked "${kb.title ?? `Knowledge #${kb.id}`}" to ${agentDisplayName}`);
      trackEvent('AgentKnowledge.Linked', { agentId: detail.agentId ?? -1, knowledgeId: kb.id });
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to link knowledge');
    } finally {
      setLinkingId(null);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      // 40% of viewport — wide enough to show description + source-count
      // chips without truncation, not so wide it covers the agent detail
      // page the user is linking from.
      width="40%"
      destroyOnClose
      title={
        <Space size={10} align="center">
          <ReadOutlined style={{ color: colors.ink[600], fontSize: 18 }} />
          <span>Link knowledge to this agent</span>
        </Space>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Pick a knowledge base to ground <b>{agentDisplayName}</b> in its content. The agent will be able
          to quote sources from any KB linked here.
        </Text>

        <Input.Search
          placeholder="Search knowledge bases by title or description…"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {error && (
          <div
            style={{
              padding: 12,
              background: isDark ? `${colors.red[700]}30` : colors.red[100],
              border: `1px solid ${colors.red[500]}`,
              borderRadius: 8,
              fontSize: 12.5,
              color: isDark ? colors.red[100] : colors.red[700],
            }}
          >
            {error}
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {isLoading ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Spin />
            </div>
          ) : candidates.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  search.trim()
                    ? 'No knowledge bases match your search.'
                    : alreadyLinkedIds.size > 0
                    ? 'Every knowledge base in this merchant is already linked to this agent.'
                    : 'No knowledge bases exist yet. Create one from the Knowledge page first.'
                }
              />
            </div>
          ) : (
            candidates.map((kb) => (
              <KnowledgeCard
                key={kb.id}
                kb={kb}
                isDark={isDark}
                isLinking={linkingId === kb.id}
                onLink={() => void handleLink(kb)}
              />
            ))
          )}
        </div>
      </div>
    </Drawer>
  );
});

// ── KB row card — title + description + source counts + Link button ────

const KnowledgeCard = function KnowledgeCard({
  kb,
  isDark,
  isLinking,
  onLink,
}: {
  kb: Knowledge;
  isDark: boolean;
  isLinking: boolean;
  onLink: () => void;
}) {
  const sourceCounts: { icon: React.ReactNode; n: number; label: string }[] = [
    { icon: <FilePdfOutlined />, n: kb.pdfCount ?? 0, label: 'PDF' },
    { icon: <FileMarkdownOutlined />, n: kb.markdownCount ?? 0, label: 'Markdown' },
    { icon: <LinkOutlined />, n: kb.linkCount ?? 0, label: 'Link' },
  ].filter((c) => c.n > 0);
  const totalSources = (kb.pdfCount ?? 0) + (kb.markdownCount ?? 0) + (kb.linkCount ?? 0);

  return (
    <div
      style={{
        padding: '12px 14px',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        background: isDark ? colors.slate[800] : '#FFFFFF',
        borderRadius: 8,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: isDark ? colors.slate[50] : colors.slate[900], marginBottom: 2 }}>
          {kb.title ?? <span style={{ fontStyle: 'italic', opacity: 0.7 }}>(untitled)</span>}
          <Text type="secondary" style={{ fontSize: 11, fontWeight: 400, marginLeft: 8 }}>
            #{kb.id}
          </Text>
        </div>
        {kb.description && (
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            {kb.description}
          </Text>
        )}
        {totalSources === 0 ? (
          <Tag style={{ fontSize: 11 }}>Empty (no sources yet)</Tag>
        ) : (
          <Space size={6} wrap>
            {sourceCounts.map((c) => (
              <Tag key={c.label} icon={c.icon} style={{ fontSize: 11 }}>
                {c.n} {c.label}
              </Tag>
            ))}
          </Space>
        )}
      </div>
      <Button
        type="primary"
        ghost
        size="small"
        icon={<PlusOutlined />}
        loading={isLinking}
        onClick={onLink}
      >
        Link
      </Button>
    </div>
  );
};
